// Planerna som byråerna betalar för, med priser i öre exklusive moms (ADR 0075
// och 0116). Två paket, och större byråer får pris på begäran. Årspriset är
// tio månader. Kontot visar dem, och modulen billing använder samma planer.

export const planIds = ['solo', 'byra'] as const;
export type PlanId = (typeof planIds)[number];

export const billingPeriods = ['month', 'year'] as const;
export type BillingPeriod = (typeof billingPeriods)[number];

/** Momsen som Stripe lägger på, en fast svensk skattesats (ADR 0075). */
export const VAT_PERCENT = 25;

export interface Plan {
  id: PlanId;
  name: string;
  description: string;
  /** Pris per månad i öre, exklusive moms. Det rabatterade priset som byrån betalar. */
  monthlyPrice: number;
  /** Ordinarie pris per månad i öre, exklusive moms, som visas överstruket (ADR 0118). */
  regularMonthlyPrice: number;
  /** Användare, eller `null` för obegränsat. */
  users: number | null;
  quotesPerMonth: number;
  forms: number;
  /** Utskick från byråns egen domän (ADR 0068). */
  senderDomain: boolean;
  popular: boolean;
}

export const plans: readonly Plan[] = [
  {
    id: 'solo',
    name: 'Solo',
    description: 'För byrån med en konsult',
    monthlyPrice: 99_900,
    regularMonthlyPrice: 179_900,
    users: 1,
    quotesPerMonth: 30,
    forms: 1,
    senderDomain: false,
    popular: false,
  },
  {
    id: 'byra',
    name: 'Byrå',
    description: 'För byråer med 2–5 konsulter',
    monthlyPrice: 199_900,
    regularMonthlyPrice: 349_900,
    users: 5,
    quotesPerMonth: 100,
    forms: 3,
    senderDomain: true,
    popular: true,
  },
];

export function isPlanId(value: unknown): value is PlanId {
  return planIds.includes(value as PlanId);
}

export function isBillingPeriod(value: unknown): value is BillingPeriod {
  return billingPeriods.includes(value as BillingPeriod);
}

export function planById(id: PlanId): Plan {
  const plan = plans.find((p) => p.id === id);
  if (!plan) throw new Error(`Okänd plan: ${id}`);
  return plan;
}

/** Vad planen kostar för perioden, i öre exklusive moms. Ett år är tio månader. */
export function planPrice(plan: Plan, period: BillingPeriod): number {
  return period === 'year' ? plan.monthlyPrice * 10 : plan.monthlyPrice;
}

/** Ordinarie pris för perioden, i öre exklusive moms. Ett år är tio månader, som det rabatterade. */
export function regularPlanPrice(plan: Plan, period: BillingPeriod): number {
  return period === 'year' ? plan.regularMonthlyPrice * 10 : plan.regularMonthlyPrice;
}

/** Momsen på ett belopp i öre, avrundad till närmaste öre. */
export function vatOn(net: number): number {
  return Math.round((net * VAT_PERCENT) / 100);
}
